import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, HelpCircle, X, Plus, Trash2, Eye, Play, Pause, Clock, RotateCcw, UserCog, Loader2, Trophy, Check, Bot, Send } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import Modal from '../../components/ui/Modal'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import { getPartnerId, getDisplayName } from '../../lib/owner'
import { useSpyLocations, useLogSpyResult } from '../../components/games/spy/hooks'
import { dealRound, MIN_PLAYERS, MAX_PLAYERS, TIMER_OPTIONS, botName } from '../../components/games/spy/logic'
import { SpyOnline } from './SpyOnline'
import { cn } from '../../lib/utils'

const HOW_TO_SEEN_KEY = 'phoenix:spy-howto-seen'

export function SpyPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user } = useAuth()
  const { locations, loading: locLoading } = useSpyLocations()
  const logResult = useLogSpyResult()
  const [logged, setLogged] = useState(false)

  const locName = (loc) => (locale === 'ar' ? loc?.name_ar : loc?.name_en) || loc?.name_en || ''
  const roleText = (loc, idx) => {
    const arr = locale === 'ar' ? loc?.roles_ar : loc?.roles_en
    return arr?.[idx] || ''
  }

  // 'setup' | 'pass' | 'timer' | 'result'
  const [phase, setPhase] = useState('setup')
  // 'local' (pass-and-play, one device) | 'online' (room codes).
  // A ?join=CODE deep link (spy-invite push) lands straight in online
  // mode; SpyOnline prefills the code from the same param.
  const [mode, setMode] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('join') ? 'online' : 'local'
    } catch { return 'local' }
  })

  // Prefill the trusted pair so the common case is one tap away.
  const initialNames = useMemo(() => {
    const me = getDisplayName(user?.id, locale)
    const partner = getDisplayName(getPartnerId(user), locale)
    return [me || '', partner || '', '']
  }, [user, locale])
  const [players, setPlayers] = useState(initialNames)
  useEffect(() => { setPlayers(initialNames) }, [initialNames])

  const [minutes, setMinutes] = useState(8)
  // Computer players that fill seats so 2 humans can play. They get a
  // role like anyone; you interrogate them during the discussion.
  const [botCount, setBotCount] = useState(0)

  // Round state. activePlayers is [{ name, isBot }]; assignments is
  // aligned to it. Bots are skipped during the pass-and-play reveal.
  const [activePlayers, setActivePlayers] = useState([])
  const [location, setLocation] = useState(null)
  const [assignments, setAssignments] = useState([])
  const [spyCount, setSpyCount] = useState(1)
  const [revealStep, setRevealStep] = useState(0)
  const [flipped, setFlipped] = useState(false)

  // Timer
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [running, setRunning] = useState(false)
  const intervalRef = useRef(null)

  const [howToOpen, setHowToOpen] = useState(() => {
    try { return !localStorage.getItem(HOW_TO_SEEN_KEY) } catch { return false }
  })
  const closeHowTo = () => {
    setHowToOpen(false)
    try { localStorage.setItem(HOW_TO_SEEN_KEY, '1') } catch { /* ignore */ }
  }

  // Timer tick
  useEffect(() => {
    if (phase !== 'timer' || !running) return undefined
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setRunning(false)
          // Time's up — buzz so the group notices without watching.
          try { navigator.vibrate?.([120, 60, 120]) } catch { /* no haptics */ }
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => clearInterval(intervalRef.current)
  }, [phase, running])

  const timeUp = phase === 'timer' && secondsLeft === 0

  const validNames = useMemo(
    () => players.map((p) => p.trim()).filter(Boolean),
    [players],
  )
  const totalPlayers = validNames.length + botCount
  const canStart = totalPlayers >= MIN_PLAYERS && totalPlayers <= MAX_PLAYERS
    && validNames.length >= 1 && !locLoading && locations.length > 0

  const setName = (i, v) => setPlayers((arr) => arr.map((p, idx) => (idx === i ? v : p)))
  const addPlayer = () => setPlayers((arr) => (validNames.length + botCount >= MAX_PLAYERS ? arr : [...arr, '']))
  const removePlayer = (i) => setPlayers((arr) => (arr.length <= 1 ? arr : arr.filter((_, idx) => idx !== i)))
  const addBot = () => setBotCount((c) => (totalPlayers >= MAX_PLAYERS ? c : c + 1))
  const removeBot = () => setBotCount((c) => Math.max(0, c - 1))

  // Indices of human players, in order — the only ones who take the
  // device during the reveal. Bots already "know" their card.
  const humanIndices = useMemo(
    () => activePlayers.map((_, i) => i).filter((i) => !activePlayers[i].isBot),
    [activePlayers],
  )

  const deal = (playerObjs) => {
    const loc = locations[Math.floor(Math.random() * locations.length)]
    const roleCount = (locale === 'ar' ? loc.roles_ar?.length : loc.roles_en?.length) || 0
    const { assignments: a, spyCount: sc } = dealRound(playerObjs.length, roleCount)
    setActivePlayers(playerObjs)
    setLocation(loc)
    setAssignments(a)
    setSpyCount(sc)
    setRevealStep(0)
    setFlipped(false)
    setLogged(false)
    setPhase('pass')
  }

  const handleOutcome = async (outcome) => {
    if (logged) return
    setLogged(true)
    // Bots are excluded from the scoreboard — it tracks humans only.
    await logResult({
      locationId: location?.id,
      locationName: locName(location),
      players: activePlayers.filter((p) => !p.isBot).map((p) => p.name),
      spyNames: activePlayers.filter((p, i) => assignments[i]?.isSpy && !p.isBot).map((p) => p.name),
      outcome,
    })
  }

  const startGame = () => {
    if (!canStart) return
    const humans = validNames.map((n) => ({ name: n, isBot: false }))
    const bots = Array.from({ length: botCount }, (_, i) => ({ name: botName(i), isBot: true }))
    deal([...humans, ...bots])
  }

  const advanceReveal = () => {
    setFlipped(false)
    if (revealStep + 1 >= humanIndices.length) {
      // Every human has seen their card — start the round timer.
      setSecondsLeft(minutes * 60)
      setRunning(true)
      setPhase('timer')
    } else {
      setRevealStep((i) => i + 1)
    }
  }

  const playAgainSamePlayers = () => deal(activePlayers)
  const backToSetup = () => { setPhase('setup'); setLocation(null) }

  const spyNames = useMemo(
    () => activePlayers.filter((_, i) => assignments[i]?.isSpy).map((p) => p.name),
    [activePlayers, assignments],
  )

  // Bots in this round + whether each is the spy, for the interrogation panel.
  const bots = useMemo(
    () => activePlayers
      .map((p, i) => ({ name: p.name, isBot: p.isBot, isSpy: !!assignments[i]?.isSpy }))
      .filter((p) => p.isBot),
    [activePlayers, assignments],
  )

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => (phase === 'setup' ? navigate('/') : backToSetup())}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('games.spy.title')}
        </h1>
        {phase === 'setup' && (
          <button
            type="button"
            onClick={() => navigate('/games/spy/scoreboard')}
            aria-label={t('games.spy.scoreboard')}
            className="p-2 rounded-md hover:bg-accent text-muted-foreground"
          >
            <Trophy className="w-5 h-5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setHowToOpen(true)}
          aria-label={t('games.spy.howToTitle')}
          className="p-2 rounded-md hover:bg-accent text-muted-foreground"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      {phase === 'setup' && (
        <>
          {/* Mode picker: one-device pass-and-play vs online room. */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button type="button" onClick={() => setMode('local')}
              className={cn('h-10 rounded-lg text-sm font-semibold', mode === 'local' ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground')} dir="auto">
              {t('games.spy.modeLocal')}
            </button>
            <button type="button" onClick={() => setMode('online')}
              className={cn('h-10 rounded-lg text-sm font-semibold', mode === 'online' ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground')} dir="auto">
              {t('games.spy.modeOnline')}
            </button>
          </div>
          {mode === 'online' ? (
            <SpyOnline />
          ) : (
            <SetupView
              players={players}
              setName={setName}
              addPlayer={addPlayer}
              removePlayer={removePlayer}
              botCount={botCount}
              addBot={addBot}
              removeBot={removeBot}
              minutes={minutes}
              setMinutes={setMinutes}
              canStart={canStart}
              locLoading={locLoading}
              totalPlayers={totalPlayers}
              onStart={startGame}
              t={t}
            />
          )}
        </>
      )}

      {phase === 'pass' && humanIndices.length > 0 && (
        <PassView
          name={activePlayers[humanIndices[revealStep]]?.name}
          assignment={assignments[humanIndices[revealStep]]}
          location={location}
          locName={locName}
          roleText={roleText}
          flipped={flipped}
          onFlip={() => { try { navigator.vibrate?.(25) } catch { /* no haptics */ } setFlipped(true) }}
          onNext={advanceReveal}
          isLast={revealStep + 1 >= humanIndices.length}
          progress={`${revealStep + 1}/${humanIndices.length}`}
          t={t}
        />
      )}

      {phase === 'timer' && (
        <div className="space-y-5 text-center">
          <p className="text-sm text-muted-foreground" dir="auto">
            {timeUp ? t('games.spy.timeUp') : t('games.spy.discussHint')}
          </p>
          <div className={cn('text-6xl font-bold tabular-nums', timeUp ? 'text-red-500 animate-pulse' : 'text-foreground')} dir="ltr">{mmss(secondsLeft)}</div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setRunning((r) => !r)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-app-primary-500 text-white text-sm font-semibold"
              dir="auto"
            >
              {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 rtl:-scale-x-100" />}
              {running ? t('games.spy.pause') : t('games.spy.resume')}
            </button>
            <button
              type="button"
              onClick={() => setSecondsLeft((s) => s + 60)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted text-foreground text-sm font-semibold"
              dir="auto"
            >
              <Clock className="w-4 h-4" /> {t('games.spy.addMinute')}
            </button>
          </div>
          <div className="flex flex-wrap justify-center gap-1.5 pt-2">
            {activePlayers.map((p, i) => (
              <span key={i} className="text-[12px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground" dir="auto">
                {p.isBot ? `🤖 ${p.name}` : p.name}
              </span>
            ))}
          </div>

          {bots.length > 0 && (
            <BotPanel bots={bots} locationName={locName(location)} t={t} />
          )}

          <button
            type="button"
            onClick={() => { setRunning(false); setPhase('result') }}
            className="w-full mt-2 py-3 rounded-xl bg-app-primary-500 text-white font-bold"
            dir="auto"
          >
            {t('games.spy.reveal')}
          </button>
        </div>
      )}

      {phase === 'result' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5 text-center space-y-3">
              <div className="text-4xl">🕵️</div>
              <div>
                <p className="text-[11px] text-muted-foreground" dir="auto">
                  {spyCount > 1 ? t('games.spy.theSpies') : t('games.spy.theSpy')}
                </p>
                <p className="text-xl font-bold text-app-primary-500" dir="auto">{spyNames.join('، ')}</p>
              </div>
              <div className="border-t border-border/60 pt-3">
                <p className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.theLocation')}</p>
                <p className="text-lg font-bold text-foreground" dir="auto">{locName(location)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Outcome capture → scoreboard. Optional; "skip" moves on
              without logging. */}
          {!logged ? (
            <Card>
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-semibold text-foreground text-center" dir="auto">{t('games.spy.whoWon')}</p>
                <button type="button" onClick={() => handleOutcome('spy_caught')} className="w-full py-2.5 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-sm font-semibold" dir="auto">
                  {t('games.spy.outcomeCaught')}
                </button>
                <button type="button" onClick={() => handleOutcome('spy_guessed')} className="w-full py-2.5 rounded-lg bg-red-500/15 text-red-500 text-sm font-semibold" dir="auto">
                  {t('games.spy.outcomeGuessed')}
                </button>
                <button type="button" onClick={() => handleOutcome('spy_escaped')} className="w-full py-2.5 rounded-lg bg-red-500/15 text-red-500 text-sm font-semibold" dir="auto">
                  {t('games.spy.outcomeEscaped')}
                </button>
                <button type="button" onClick={() => setLogged(true)} className="w-full py-1.5 text-xs text-muted-foreground" dir="auto">
                  {t('games.spy.skipLog')}
                </button>
              </CardContent>
            </Card>
          ) : (
            <p className="flex items-center justify-center gap-1.5 text-sm text-emerald-500" dir="auto">
              <Check className="w-4 h-4" /> {t('games.spy.logged')}
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={backToSetup}
              className="flex-1 py-3 rounded-xl border-2 border-border text-foreground text-sm font-semibold"
              dir="auto"
            >
              {t('games.spy.newSetup')}
            </button>
            <button
              type="button"
              onClick={playAgainSamePlayers}
              className="flex-1 py-3 rounded-xl bg-app-primary-500 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"
              dir="auto"
            >
              <RotateCcw className="w-4 h-4" /> {t('games.spy.playAgain')}
            </button>
          </div>
        </div>
      )}

      <Modal open={howToOpen} onClose={closeHowTo} ariaLabel={t('games.spy.howToTitle')}>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">{t('games.spy.howToTitle')}</h2>
          <button type="button" onClick={closeHowTo} aria-label={t('settings.close')} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ol className="space-y-2 text-sm text-foreground list-decimal ps-5" dir="auto">
          <li>{t('games.spy.how1')}</li>
          <li>{t('games.spy.how2')}</li>
          <li>{t('games.spy.how3')}</li>
          <li>{t('games.spy.how4')}</li>
        </ol>
        <button type="button" onClick={closeHowTo} className="w-full mt-4 py-2 rounded-md bg-app-primary-500 text-white text-sm font-semibold" dir="auto">
          {t('games.spy.gotIt')}
        </button>
      </Modal>
    </div>
  )
}

function SetupView({ players, setName, addPlayer, removePlayer, botCount, addBot, removeBot, minutes, setMinutes, canStart, locLoading, totalPlayers, onStart, t }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">
            {t('games.spy.players')} ({totalPlayers}/{MAX_PLAYERS})
          </p>
          {players.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={p}
                onChange={(e) => setName(i, e.target.value)}
                placeholder={t('games.spy.playerPlaceholder', { n: i + 1 })}
                className="flex-1 h-10 px-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none"
              />
              {players.length > 1 && (
                <button type="button" onClick={() => removePlayer(i)} aria-label={t('games.spy.removePlayer')} className="p-2 rounded-md hover:bg-accent text-muted-foreground">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          {totalPlayers < MAX_PLAYERS && (
            <button type="button" onClick={addPlayer} className="inline-flex items-center gap-1.5 text-sm text-app-primary-500 mt-1" dir="auto">
              <Plus className="w-4 h-4" /> {t('games.spy.addPlayer')}
            </button>
          )}

          {/* Computer players — fill seats so 2 humans can play. */}
          <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-border/60">
            <div className="flex items-center gap-2 min-w-0">
              <Bot className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
              <span className="text-sm text-foreground truncate" dir="auto">{t('games.spy.computerPlayers')}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button type="button" onClick={removeBot} disabled={botCount <= 0} aria-label={t('games.spy.removeBot')} className="w-8 h-8 rounded-full bg-muted text-foreground disabled:opacity-40 inline-flex items-center justify-center">−</button>
              <span className="w-5 text-center text-sm font-semibold tabular-nums">{botCount}</span>
              <button type="button" onClick={addBot} disabled={totalPlayers >= MAX_PLAYERS} aria-label={t('games.spy.addBot')} className="w-8 h-8 rounded-full bg-muted text-foreground disabled:opacity-40 inline-flex items-center justify-center">+</button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.roundTime')}</p>
          <div className="grid grid-cols-3 gap-2">
            {TIMER_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMinutes(m)}
                className={cn(
                  'h-10 rounded-lg text-sm font-semibold',
                  minutes === m ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground',
                )}
                dir="auto"
              >
                {t('games.spy.minutes', { n: m })}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <button
        type="button"
        onClick={onStart}
        disabled={!canStart}
        className="w-full py-3.5 rounded-xl bg-app-primary-500 text-white font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
        dir="auto"
      >
        {locLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 rtl:-scale-x-100" />}
        {t('games.spy.start')}
      </button>
      {totalPlayers < MIN_PLAYERS && (
        <p className="text-[12px] text-muted-foreground text-center" dir="auto">{t('games.spy.needMore', { n: MIN_PLAYERS })}</p>
      )}
    </div>
  )
}

// Discussion feed for the one-phone game. Bots take turns asking on
// their own (round-robin, every ~20s or on tap); their lines drop into
// a shared feed everyone reads. You can type a reply and the last bot
// that asked answers back — so it's a real back-and-forth, no per-bot
// buttons.
function BotPanel({ bots, locationName, t }) {
  const [feed, setFeed] = useState([]) // { id, name, kind: 'q'|'a'|'me', text }
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState(false) // a bot is waiting for an answer
  const [target, setTarget] = useState(0) // which bot the reply goes to
  const rotRef = useRef(0)
  const feedEndRef = useRef(null)
  const busyRef = useRef(false)
  const inputRef = useRef(null)

  // When the reply box is focused, the on-screen keyboard otherwise
  // leaves a big empty band below this short page. Scroll the input
  // into the centre of the (shrunk) viewport so the keyboard sits
  // right under it.
  const onInputFocus = () => {
    setTimeout(() => inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
  }

  const push = (entry) => setFeed((f) => [...f, { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, ...entry }])

  const callBot = async (bot, mode, question) => {
    const resp = await supabase.functions.invoke('spy-bot-answer', {
      body: { location: bot.isSpy ? null : locationName, is_spy: bot.isSpy, mode, question: question || undefined },
    })
    return resp.data?.answer || t('games.spy.botFailed')
  }

  // A bot asks the group. After asking it WAITS — no new auto-question
  // fires until the player answers (or taps "ask now").
  const nextBotAsks = useCallback(async () => {
    if (busyRef.current || !bots.length) return
    busyRef.current = true; setBusy(true)
    const idx = rotRef.current % bots.length
    const bot = bots[idx]
    rotRef.current += 1
    try {
      const text = await callBot(bot, 'ask')
      push({ name: bot.name, kind: 'q', text })
      setTarget(idx) // default the reply at whoever just asked
      setPending(true)
    } finally { busyRef.current = false; setBusy(false) }
  }, [bots, locationName]) // eslint-disable-line

  // Kick off the first question once.
  useEffect(() => { if (bots.length) nextBotAsks() }, [bots.length]) // eslint-disable-line

  useEffect(() => { feedEndRef.current?.scrollIntoView({ block: 'end' }) }, [feed])

  const sendReply = async () => {
    const text = input.trim()
    if (!text || busyRef.current) return
    setInput('')
    push({ name: t('games.spy.you'), kind: 'me', text })
    const bot = bots[target] || bots[0]
    if (!bot) return
    busyRef.current = true; setBusy(true)
    try {
      const ans = await callBot(bot, 'answer', text)
      push({ name: bot.name, kind: 'a', text: ans })
      setPending(false) // answered → the next bot may ask now
    } finally { busyRef.current = false; setBusy(false) }
  }

  return (
    <Card className="text-start">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" dir="auto">{t('games.spy.discussion')}</p>
          <button type="button" onClick={nextBotAsks} disabled={busy || pending}
            className="text-[11px] text-app-primary-500 font-semibold inline-flex items-center gap-1 disabled:opacity-40" dir="auto">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HelpCircle className="w-3.5 h-3.5" />} {t('games.spy.nextQuestion')}
          </button>
        </div>
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {feed.length === 0 && (
            <p className="text-[12px] text-muted-foreground" dir="auto">{t('games.spy.feedHint')}</p>
          )}
          {feed.map((e) => (
            <div key={e.id} className={cn('text-sm rounded-lg px-2.5 py-1.5',
              e.kind === 'me' ? 'bg-app-primary-500 text-white ms-8' :
              e.kind === 'q' ? 'bg-app-primary-500/10 text-foreground' : 'bg-muted text-foreground')} dir="auto">
              {e.kind !== 'me' && <span className="font-semibold">{e.kind === 'q' ? `🤖❓ ${e.name}: ` : `🤖 ${e.name}: `}</span>}
              {e.text}
            </div>
          ))}
          <div ref={feedEndRef} />
        </div>
        {/* Pick which bot you're answering. */}
        {bots.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {bots.map((b, i) => (
              <button key={i} type="button" onClick={() => setTarget(i)}
                className={cn('px-2.5 h-7 rounded-full text-[12px] font-semibold', target === i ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground')} dir="auto">
                {b.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={onInputFocus}
            onKeyDown={(e) => { if (e.key === 'Enter') sendReply() }}
            placeholder={t('games.spy.replyToTarget', { name: bots[target]?.name || '' })}
            className="flex-1 h-9 px-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none"
            dir="auto"
          />
          <button type="button" onClick={sendReply} disabled={busy || !input.trim()}
            className="px-3 h-9 rounded-md bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50">
            <Send className="w-4 h-4 rtl:-scale-x-100" />
          </button>
        </div>
      </CardContent>
    </Card>
  )
}

function PassView({ name, assignment, location, locName, roleText, flipped, onFlip, onNext, isLast, progress, t }) {
  const isSpy = assignment?.isSpy
  return (
    <div className="space-y-4">
      <p className="text-center text-xs text-muted-foreground" dir="auto">{progress}</p>
      <p className="text-center text-lg font-bold text-foreground" dir="auto">
        {t('games.spy.passTo', { name })}
      </p>

      <div className="[perspective:1200px]">
        <button
          type="button"
          onClick={() => !flipped && onFlip()}
          className="relative w-full h-[55vh] [transform-style:preserve-3d] transition-transform duration-500"
          style={{ transform: flipped ? 'rotateY(180deg)' : 'none' }}
          aria-label={t('games.spy.tapToReveal')}
        >
          {/* Front (face down) */}
          <div className="absolute inset-0 [backface-visibility:hidden] rounded-2xl bg-app-primary-500/10 border-2 border-app-primary-500/40 flex flex-col items-center justify-center gap-3">
            <Eye className="w-12 h-12 text-app-primary-500" />
            <span className="text-sm font-semibold text-app-primary-500" dir="auto">{t('games.spy.tapToReveal')}</span>
          </div>
          {/* Back (the secret) */}
          <div
            className={cn(
              'absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl border-2 flex flex-col items-center justify-center gap-3 p-6 text-center',
              isSpy ? 'bg-red-500/10 border-red-500/50' : 'bg-card border-app-primary-500/40',
            )}
          >
            {isSpy ? (
              <>
                <div className="text-5xl">🕵️</div>
                <span className="text-2xl font-bold text-red-500" dir="auto">{t('games.spy.youAreSpy')}</span>
                <span className="text-sm text-muted-foreground" dir="auto">{t('games.spy.spyHint')}</span>
              </>
            ) : (
              <>
                <span className="text-[11px] text-muted-foreground" dir="auto">{t('games.spy.theLocation')}</span>
                <span className="text-2xl font-bold text-foreground" dir="auto">{locName(location)}</span>
                {assignment?.roleIndex != null && roleText(location, assignment.roleIndex) && (
                  <span className="inline-flex items-center gap-1.5 mt-1 text-sm text-app-primary-500" dir="auto">
                    <UserCog className="w-4 h-4" /> {t('games.spy.yourRole', { role: roleText(location, assignment.roleIndex) })}
                  </span>
                )}
              </>
            )}
          </div>
        </button>
      </div>

      {flipped && (
        <button
          type="button"
          onClick={onNext}
          className="w-full py-3 rounded-xl bg-app-primary-500 text-white font-bold"
          dir="auto"
        >
          {isLast ? t('games.spy.startRound') : t('games.spy.hideAndPass')}
        </button>
      )}
    </div>
  )
}

export default SpyPage
